/**
 * Pure path helpers for Home, mirroring inboxPaths and regulationPaths.
 *
 * Home is the organization's landing page: `/$org`. The command center
 * (`/$org/command-center`), the agents (`/$org/agents`) and the skills
 * (`/$org/skills`, `/$org/skills/$slug`) stay under Home in the chrome: the
 * rail keeps Home selected and the Home sidebar stays beside them. Spaces
 * (`/$org/spaces`) also sits beside Home's sidebar, whose Spaces group links
 * to it, but has its own rail item (`isSpacesPathname`). Workflows
 * (`/$org/workflows`, `/$org/workflows/$workflowId`) are Home pages too, but
 * full width with no sidebar beside them, the rail's Workflows item selected
 * (`isWorkflowsPathname`). Apps
 * (`/$org/apps/…`) are their own area (components/apps/appsPaths). Chat moved from `/$org`
 * to `/$org/chat`, which starts or resumes the org's new-chat draft.
 *
 * @module components/home/homePaths
 */

/**
 * First path segments that are app routes, never an organization slug. An
 * org-less `/inbox` must not read as the Home of an org called "inbox".
 */
const RESERVED_TOP_LEVEL_SEGMENTS: ReadonlySet<string> = new Set([
  "automations",
  "compliance",
  "connect",
  "connections",
  "inbox",
  "library",
  "pair",
  "regulation",
  "settings",
  "usage",
]);

/** `/$org/command-center`: a Home page, under Home on the rail and beside Home's sidebar. */
export const COMMAND_CENTER_SEGMENT = "command-center";
/** `/$org/agents`: the agents that work for the organization, a Home page. */
export const AGENTS_SEGMENT = "agents";
/** `/$org/skills` and `/$org/skills/$slug`: the skill leaderboard and a skill, Home pages. */
export const SKILLS_SEGMENT = "skills";

/**
 * `/$org/spaces`: every space in the organization; `/$org/spaces/$spaceId`:
 * one space. Both beside Home's sidebar with their own rail item.
 */
export const SPACES_SEGMENT = "spaces";

/** `/$org/workflows` and `/$org/workflows/$workflowId`: full width (no sidebar), with their own rail item. */
export const WORKFLOWS_SEGMENT = "workflows";

/** Which Home page a path shows: the landing, or a page beside Home's sidebar. */
export type HomePage =
  | "landing"
  | "command-center"
  | "agents"
  | "skills"
  | "skill"
  | "spaces"
  | "space"
  | "workflows"
  | "workflow";

/** The Home page a pathname shows, or null when it is not a Home page. */
export function homePageOf(pathname: string): HomePage | null {
  const segments = pathname.split("/").filter(Boolean);
  const [org, section] = segments;
  if (org === undefined || RESERVED_TOP_LEVEL_SEGMENTS.has(org)) return null;
  if (segments.length === 1) return "landing";
  if (segments.length === 2) {
    if (section === COMMAND_CENTER_SEGMENT) return "command-center";
    if (section === AGENTS_SEGMENT) return "agents";
    if (section === SKILLS_SEGMENT) return "skills";
    if (section === SPACES_SEGMENT) return "spaces";
    if (section === WORKFLOWS_SEGMENT) return "workflows";
    return null;
  }
  if (segments.length !== 3) return null;
  if (section === SKILLS_SEGMENT) return "skill";
  if (section === WORKFLOWS_SEGMENT) return "workflow";
  return section === SPACES_SEGMENT ? "space" : null;
}

/** True on `/$org` and the pages beside Home's sidebar; drives nav selection, never content. */
export function isHomePathname(pathname: string): boolean {
  return homePageOf(pathname) !== null;
}

/**
 * True on `/$org/spaces` and `/$org/spaces/$spaceId`. The pages sit beside
 * Home's sidebar, but the rail selects Spaces there, not Home, so the rail
 * asks this before `isHomePathname`.
 */
export function isSpacesPathname(pathname: string): boolean {
  const page = homePageOf(pathname);
  return page === "spaces" || page === "space";
}

/** The space `/$org/spaces/$spaceId` shows, or null on any other path. */
export function spaceIdOfPathname(pathname: string): string | null {
  if (homePageOf(pathname) !== "space") return null;
  const segment = pathname.split("/").filter(Boolean)[2];
  if (segment === undefined) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * Home's old `?space=<id>` view (Home filtered to one space) is the space's
 * own page now: the space id an old Home link names, which the Home route
 * redirects to `/$org/spaces/$spaceId`, or null when it names none.
 */
export function legacyHomeSpaceId(search: Record<string, unknown>): string | null {
  const space = search.space;
  return typeof space === "string" && space.trim().length > 0 ? space.trim() : null;
}

/** True on the Workflows list and a workflow's page; the rail selects Workflows there, not Home. */
export function isWorkflowsPathname(pathname: string): boolean {
  const page = homePageOf(pathname);
  return page === "workflows" || page === "workflow";
}
